import type { FeatureFlagKey } from '@erp/shared-types';
import type CurrentUser from 'src/types/CurrentUser';

type ImitationState = NonNullable<CurrentUser['imitation']>;

/**
 * Module singleton mirroring the `imitation` block from `/auth/me`.
 *
 * Same shape as `notify403`'s `setNotify403IsAdmin`: the write blocks and the
 * feature-flag resolvers run outside React (fetch overrides, a MutationCache
 * callback, a plain function called from the RPC batching plugin), so they
 * cannot read context. `useAuth` seeds this during render — before the first
 * render that could read a flag — rather than in an effect.
 */
let _imitation: ImitationState | null = null;

export function setImitationState(
  state: CurrentUser['imitation'] | null,
): void {
  _imitation = state ?? null;
}

export function isImitationActive(): boolean {
  return _imitation !== null;
}

/**
 * The imitated user's value for `flag`, or `undefined` when imitation is
 * inactive or the backend didn't evaluate that flag.
 */
export function getImitationFlag(
  flag: FeatureFlagKey,
): boolean | string | undefined {
  return _imitation?.flags[flag];
}

/**
 * The imitated user's whole flag map, or `null` when nobody is imitating. The
 * same object until `/auth/me` replaces it, so it can key a memo.
 */
export function getImitationFlags(): ImitationState['flags'] | null {
  return _imitation?.flags ?? null;
}

/**
 * Discriminator the backend puts on every blocked write — `data.reason` on
 * oRPC `FORBIDDEN` errors, `code` on legacy REST 403 bodies. Matched on
 * instead of the human message, which is translatable and may drift.
 */
export const IMITATION_VIEW_ONLY_REASON = 'IMITATION_VIEW_ONLY';

/**
 * Thrown by the client-side write blocks so error handlers can recognise a
 * vetoed write and stay quiet — `notifyViewOnly()` has already toasted.
 */
export class ImitationViewOnlyError extends Error {
  constructor(message = 'This is only a view mode') {
    super(message);
    this.name = 'ImitationViewOnlyError';
    // Required for `instanceof` to survive the ES5 target downlevelling.
    Object.setPrototypeOf(this, ImitationViewOnlyError.prototype);
  }
}

function hasViewOnlyMarker(value: unknown): boolean {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  if ('reason' in value && value.reason === IMITATION_VIEW_ONLY_REASON) {
    return true;
  }
  return 'code' in value && value.code === IMITATION_VIEW_ONLY_REASON;
}

/**
 * True for anything the imitation guard produced: the client-side veto, an
 * oRPC `FORBIDDEN` carrying `data.reason`, or a legacy REST 403 whose parsed
 * body is hung off `ApiError.responseData`.
 */
export function isImitationViewOnlyError(error: unknown): boolean {
  if (error instanceof ImitationViewOnlyError) {
    return true;
  }
  if (typeof error !== 'object' || error === null) {
    return false;
  }
  if ('data' in error && hasViewOnlyMarker(error.data)) {
    return true;
  }
  return 'responseData' in error && hasViewOnlyMarker(error.responseData);
}
